import { ApolloClient, ApolloLink, ApolloProvider, InMemoryCache, from } from '@apollo/client';

import { onError } from '@apollo/client/link/error';

import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';

import APP_CONFIG from '@/_config/config.app';
import { useToast } from '@/hooks/use-toast/use-toast.hook';
import { setContext } from '@apollo/client/link/context';
import { IPublicClientApplication } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { ASTNode, stripIgnoredCharacters } from 'graphql';
import { useMemo } from 'react';
import { useBusinessUnitStore } from '@/store/business-unit.store';
import { useWorkAsStore } from '@/store/work-as.store';

type Props = {
  children: React.ReactNode;
};

const SUBSTITUTION_NOT_ALLOWED_ERROR_MESSAGE = 'Substitution is not allowed.';

const uploadLink = createUploadLink({
  uri: import.meta.env.VITE_GQL_URL,
  print(ast: ASTNode, originalPrint: (ast: ASTNode) => string) {
    // `stripIgnoredCharacters` minifies the GraphQL query body.
    return stripIgnoredCharacters(originalPrint(ast));
  },
  headers: {
    'Apollo-Require-Preflight': 'true',
  },
});

const errorLink = (
  toast: (params: { description: string; variant: 'error' }) => void,
  onSubstitutionCleared: () => void,
) =>
  onError(({ graphQLErrors, networkError, operation }) => {
    const { activeSubstitution, clearActiveSubstitution } = useWorkAsStore.getState();
    const hasSubstitutionNotAllowedError =
      !!activeSubstitution &&
      !!graphQLErrors?.some(({ message }) => message.trim() === SUBSTITUTION_NOT_ALLOWED_ERROR_MESSAGE);

    if (hasSubstitutionNotAllowedError) {
      // If the error indicates that substitution is not allowed, we clear the active substitution context and
      // refetch queries to update the UI accordingly.
      clearActiveSubstitution();
      onSubstitutionCleared();
    }

    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) =>
        toast({
          description: `GQL error. ${operation.operationName} [GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
          variant: 'error',
        }),
      );
    }
    if (networkError) {
      toast({
        description: `Network error. ${operation.operationName} ${networkError}`,
        variant: 'error',
      });
    }
  });

const consoleLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((data) => {
    return data;
  });
});

const authLink = (instance: IPublicClientApplication) =>
  setContext(async (_, { headers }) => {
    await instance.initialize();
    const accounts = instance.getAllAccounts();
    const { activeBusinessUnit } = useBusinessUnitStore.getState();
    const { activeSubstitution } = useWorkAsStore.getState();

    const exp = accounts[0]?.idTokenClaims?.exp || 0;
    const currentTime = Math.floor(Date.now() / 1000);
    const timeLeft = exp - currentTime;
    const expired = timeLeft <= 60;

    const token = await instance
      .acquireTokenSilent({ ...APP_CONFIG.TOKEN_REQUEST, account: accounts[0], forceRefresh: expired })
      .catch(async () => {
        instance.logoutRedirect({
          account: accounts[0],
          postLogoutRedirectUri: '/login',
          onRedirectNavigate: () => {
            return false;
          },
        });
      });

    instance.setActiveAccount(accounts[0]);

    return {
      headers: {
        ...headers,
        Authorization: token ? `Bearer ${token.accessToken}` : null,
        'x-active-business-unit': activeBusinessUnit ?? null,
        ...(activeSubstitution ? { 'x-active-substitution': activeSubstitution.initiatorId } : {}),
      },
    };
  });

export const GraphQLContainer = ({ children }: Props) => {
  const { toast } = useToast();
  const { instance } = useMsal();

  const client = useMemo(() => {
    let apolloClient: ApolloClient<object> | null = null;
    const onSubstitutionCleared = () => {
      if (!apolloClient) {
        return;
      }
      void apolloClient.refetchQueries({
        include: 'active',
      });
    };

    apolloClient = new ApolloClient({
      link: from([consoleLink, errorLink(toast, onSubstitutionCleared), authLink(instance), uploadLink]),
      cache: new InMemoryCache({
        typePolicies: {
          Product: {
            fields: {
              tailoringProcess: {
                merge(existing, incoming, { mergeObjects }) {
                  return mergeObjects(existing, incoming);
                },
              },
            },
          },
          YesNoQuestion: {
            // due to neo4j returning same idså
            // we need to tell apollo how to identify the objects for caching
            keyFields: ['id', 'productId', 'updatedAfterLastInteraction'],
          },
          ProductBuildingBlock: {
            // due to neo4j returning same ids
            // we need to tell apollo how to identify the objects for caching
            keyFields: ['id', 'productId'],
          },
          ConfigurationGroup: {
            keyFields: ['id', 'productId'],
          },
          UniversalDesignObligation: {
            keyFields: false,
          },
          UniversalBuSpecificContent: {
            keyFields: false,
          },
          UniversalBuildingBlock: {
            keyFields: false,
          },
          UniversalDataBlock: {
            keyFields: false,
          },
          UniversalTailoringCriterion: {
            keyFields: false,
          },
          UniversalMaturityStage: {
            keyFields: false,
          },
          UniversalMaturityLevelSet: {
            keyFields: false,
          },
          UniversalMaturityLevelSequence: {
            keyFields: false,
          },
          UniversalCompetency: {
            keyFields: false,
          },
          UniversalInformation: {
            keyFields: false,
          },
          UniversalConfigurationGroup: {
            keyFields: false,
          },
          UniversalOperator: {
            keyFields: false,
          },
          ApplicationUserGroup: {
            keyFields: false,
          },
          BusinessUnit: {
            keyFields: false,
          },
        },
      }),
    });
    return apolloClient;
  }, []);
  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
